import { faLongArrowAltUp } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled, { keyframes } from 'styled-components';

import {
  ManagerProgression,
  ShopItemType,
} from '@sorare/core/src/__generated__/globalTypes';
import { EmojiFlood } from '@sorare/core/src/atoms/animations/EmojiFlood';
import Button from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { HeadlineL, HeadlineS } from '@sorare/core/src/atoms/typography';
import { glossary } from '@sorare/core/src/lib/glossary';
import {
  desktopAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';
import { createVar } from '@sorare/core/src/style/utils';

import { GenericSkinItem } from 'components/challenges/Category/Task';
import { ExperienceBarStep } from 'components/challenges/ClaimBadgeAnimation/types';
import {
  LevelBar,
  TRANSITION_DURATION,
} from 'components/user/progression/LevelBar';

import e from './assets/e.svg';
import exclamation from './assets/exclamation.svg';
import l from './assets/l.svg';
import p from './assets/p.svg';
import u from './assets/u.svg';
import v from './assets/v.svg';
import { computeLevelsToAnimate } from './utils';

const BEFORE_START_DURATION = 0.2;
const LEVEL_UP_DURATION = 1.5;
const FADE_OUT_DURATION = 0.4;

const rewardBlocKIndex = createVar();

const Root = styled(Vertical).attrs({ center: true })`
  position: absolute;
  inset: 0;
`;
const Content = styled(Vertical).attrs({ center: true })`
  --frame-spacing: 100px;
  text-align: center;
  gap: var(--double-unit);
`;
const slideIn = keyframes`
0% {
  opacity: 0;
  transform: translateY(50px);
}
100% {
  opacity: 1;
  transform: translateY(0);
}`;
const ManagerLevel = styled(HeadlineL).attrs({ as: 'p' })`
  font-family:
    Druk Wide,
    sans-serif;
  font-weight: bold;
  animation: 0.6s var(--c-cb-ease-out) ${slideIn} backwards;

  &.levelUp {
    animation: 0.6s ${1.5 * TRANSITION_DURATION}s ${slideIn} reverse forwards;
  }
`;

const fadeOutInCorner = keyframes`
0% {
  opacity: 1;
}
100% {
  opacity: 0;
  transform: translate(40vw, -40vh);
}
`;
const StyledLevelBar = styled(LevelBar)`
  width: 500px;
  max-width: 90vw;
  height: calc(5 * var(--unit));
  z-index: 1;

  .ended & {
    animation: ${FADE_OUT_DURATION}s ${fadeOutInCorner} forwards;
  }

  transition: transform 0.8s var(--c-cb-ease-out);
  .REWARDS & {
    transform: translateY(-150px);
  }
`;
const Points = styled(HeadlineL).attrs({ as: 'p' })`
  font-family:
    Druk Wide,
    sans-serif;
  font-weight: bold;
  animation:
    0.6s 0.5s var(--c-cb-ease-out) ${slideIn} backwards,
    0.6s ${0.5 + TRANSITION_DURATION}s var(--c-cb-ease-out) ${slideIn} reverse
      forwards;
`;
const scaleIn = keyframes`
from {
  transform: scaleY(3);
}
to {
  transform: scaleY(1)
}`;
const fadeOut = keyframes`
100% {
  opacity: 0;
}`;
const YellowFog = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    inset: 0;

    background: linear-gradient(
      rgba(108, 93, 14, 0.8) 0%,
      transparent 20%,
      transparent 70%,
      rgba(108, 93, 14, 0.8) 100%
    );

    animation: 0.6s var(--c-cb-ease-out) ${scaleIn} forwards;

    .ended & {
      animation: 0.6s var(--c-cb-ease-out) ${scaleIn} forwards reverse;
    }
  }
`;
const LevelUpContainer = styled(Horizontal).attrs({ gap: 0, center: true })`
  position: absolute;
  inset: 0;
  z-index: 0;
  overflow: hidden;

  animation: 0.4s var(--c-cb-ease-in-xl) ${LEVEL_UP_DURATION}s ${fadeOut}
    forwards;
`;
const expandLetters = keyframes`
0% {
  --gap: 0.4vmax;
  scale: 1.1;
}
100% {
  --gap: 2vmax;
  scale: 1;
}`;
const blink = keyframes`
0% {
  opacity: 1;
}
50% {
  opacity: 0;
}
100% {
  opacity: 1;
}`;
const growLine = keyframes`
from {
  width: 0%
}
to {
  width: 100%
}
`;
const LevelUpText = styled(Horizontal)`
  --gap: 0.4vmax; /* fallback for browsers not supporting @property */
  @property --gap {
    syntax: '<length>';
    initial-value: 0.4vmax;
    inherits: false;
  }

  width: 100%;
  position: relative;
  justify-content: center;
  gap: calc(3 * var(--gap));
  animation: 0.7s var(--c-cb-ease-out) ${expandLetters} forwards;

  margin-bottom: 130px;
  --offset-top-y: -80px;
  --offset-bottom-y: -120px;

  & > * {
    gap: var(--gap);
    animation:
      0.7s var(--c-cb-ease-out) ${expandLetters} forwards,
      0.15s 1.05s 3 ${blink};
  }

  @media ${tabletAndAbove} {
    margin-bottom: 0;
    --offset-top-y: -80px;
    --offset-bottom-y: -80px;
    gap: calc(4 * var(--gap));

    & > * {
      gap: calc(2 * var(--gap));
    }
  }

  @media ${desktopAndAbove} {
    gap: calc(6 * var(--gap));

    & > * {
      gap: calc(2 * var(--gap));
    }
  }

  &:before,
  &:after {
    content: '';
    width: 0;
    height: 2px;
    background-color: var(--c-neutral-1000);
    position: absolute;
    animation:
      0.6s var(--c-cb-ease-in-xl) ${growLine} forwards,
      0.6s 1.4s var(--c-cb-ease-in-xl) ${growLine} forwards reverse;
  }
  &:before {
    left: 0;
    top: var(--offset-top-y);
  }
  &:after {
    right: 0;
    bottom: var(--offset-bottom-y);
  }
`;
const Letter = styled.img`
  width: 3vmax;
  aspect-ratio: 1/2.5;
  @media ${tabletAndAbove} {
    width: 5vmax;
  }
`;

const rewardAppearFromBottom = keyframes`
0% {
  opacity: 0;
  transform: translateY(200px);
}
100% {
  opacity: 1;
  transform: translateY(0);
}`;
const RewardsContainer = styled.div`
  position: absolute;
  transform: translateY(80px);

  .ended & {
    animation: 0.2s ${fadeOut} forwards;
  }
`;
const Rewards = styled(Horizontal)`
  align-self: center;
`;
const RewardTitle = styled(HeadlineL).attrs({ as: 'p' })`
  font-family:
    Druk Wide,
    sans-serif;
  font-weight: bold;
  text-transform: uppercase;

  animation: 0.8s 0.2s var(--c-cb-ease-out) ${rewardAppearFromBottom} both;
`;
const RewardBlock = styled(Vertical)`
  --delay: calc(var(${rewardBlocKIndex}) * 0.3s);
`;
const RewardImgContainer = styled.div`
  position: relative;

  animation: 0.8s calc(0.4s + var(--delay)) var(--c-cb-ease-out)
    ${rewardAppearFromBottom} both;

  &:before {
    content: '';
    position: absolute;
    inset: 50%;
    box-shadow: 0 0 80px 50px rgba(108, 93, 14, 1);
  }
`;
const RewardImg = styled.img`
  width: 100px;
  border-radius: var(--unit);
  object-fit: cover;
  aspect-ratio: 1;
  position: relative;
`;
const RewardCount = styled(HeadlineS).attrs({ as: 'p' })`
  font-family:
    Druk Wide,
    sans-serif;
  font-weight: bold;

  animation: 0.8s calc(0.6s + var(--delay)) var(--c-cb-ease-out)
    ${rewardAppearFromBottom} both;
`;
const RewardText = styled(HeadlineS).attrs({ as: 'p' })`
  font-family:
    Druk Wide,
    sans-serif;
  font-weight: bold;
  text-transform: uppercase;

  animation: 0.8s calc(0.8s + var(--delay)) var(--c-cb-ease-out)
    ${rewardAppearFromBottom} both;
`;
const ContinueBtn = styled(Button)`
  margin: 0 auto;
  animation: 0.8s 1s var(--c-cb-ease-out) ${rewardAppearFromBottom} both;
`;

type ManagerProgressionState = Pick<
  ManagerProgression,
  'points' | 'pointsForNextLevel' | 'levelReached'
>;

export type Props = {
  step: ExperienceBarStep;
  setStep: (step: ExperienceBarStep) => void;
  from: ManagerProgressionState;
  to: ManagerProgressionState;
  pointsEarned: number;
  rewards: GenericSkinItem[];
  onEnd: () => void;
};

let timeout1: NodeJS.Timeout;
let timeout2: NodeJS.Timeout;
let timeout3: NodeJS.Timeout;
let timeout4: NodeJS.Timeout;

export const ExperienceBar = ({
  step,
  setStep,
  from,
  to,
  pointsEarned,
  rewards,
  onEnd,
}: Props) => {
  const [levelStep, setLevelStep] = useState(0);
  const [ended, setEnded] = useState(false);
  const [animated, setAnimated] = useState(true);

  const levelsToAnimate = useMemo(
    () => computeLevelsToAnimate(from, to),
    [from, to]
  );

  const [managerProgression, setManagerProgression] =
    useState<ManagerProgressionState>(levelsToAnimate[levelStep].from);
  const [animatedPoints, setAnimatedPoints] = useState<number>(
    levelsToAnimate[levelStep].from.points
  );

  const endAnimation = useCallback(() => {
    setEnded(true);
    setTimeout(onEnd, FADE_OUT_DURATION * 1000);
  }, [onEnd]);

  useEffect(() => {
    const leveledUp = levelsToAnimate[levelStep].levelUp;

    const time1 =
      BEFORE_START_DURATION + (levelStep === 0 ? BEFORE_START_DURATION : 0);
    timeout1 = setTimeout(() => {
      setAnimatedPoints(levelsToAnimate[levelStep].to.points);
    }, time1 * 1000);

    const time2 = time1 + TRANSITION_DURATION;
    timeout2 = setTimeout(() => {
      setManagerProgression(levelsToAnimate[levelStep].to);
    }, time2 * 1000);

    let time3 = time2 + TRANSITION_DURATION;
    if (levelStep !== levelsToAnimate.length - 1) {
      timeout3 = setTimeout(() => {
        setManagerProgression(levelsToAnimate[levelStep + 1].from);
        setAnimatedPoints(0);
        setLevelStep(prevStep => prevStep + 1);
      }, time3 * 1000);
    } else if (leveledUp) {
      time3 *= 0.5;
      timeout3 = setTimeout(() => {
        setStep(ExperienceBarStep.LEVELUP);
      }, time3 * 1000);

      const time4 = time3 + LEVEL_UP_DURATION;
      if (rewards.length) {
        timeout4 = setTimeout(() => {
          setStep(ExperienceBarStep.REWARDS);
        }, time4 * 1000);
      } else {
        timeout4 = setTimeout(() => {
          endAnimation();
        }, time4 * 1000);
      }
    } else {
      timeout3 = setTimeout(() => {
        endAnimation();
      }, time3 * 1000);
    }

    return () => {
      clearTimeout(timeout1);
      clearTimeout(timeout2);
      clearTimeout(timeout3);
      clearTimeout(timeout4);
    };
  }, [onEnd, to, levelStep, levelsToAnimate, rewards, endAnimation, setStep]);

  useEffect(() => {
    if (step === ExperienceBarStep.REWARDS) {
      if (managerProgression.levelReached !== to.levelReached) {
        setAnimated(false);
        setManagerProgression(levelsToAnimate[levelsToAnimate.length - 1].to);
      }
      clearTimeout(timeout1);
      clearTimeout(timeout2);
      clearTimeout(timeout3);
      clearTimeout(timeout4);
    }
  }, [levelsToAnimate, managerProgression.levelReached, step, to.levelReached]);

  const backgroundsWon = rewards.filter(
    item => item.position === ShopItemType.BACKGROUND
  );
  const clubBadgesWon = rewards.filter(
    item => item.position === ShopItemType.CLUB_BADGE
  );

  return (
    <Root className={classNames(step, { ended })}>
      <Content>
        {step !== ExperienceBarStep.REWARDS && (
          <ManagerLevel
            className={classNames({
              levelUp: levelsToAnimate[levelStep].levelUp,
            })}
          >
            <FormattedMessage
              id="Challenges.ClaimBadgeAnimation.ExperienceBar.managerLevel"
              defaultMessage="Manager Level"
            />
          </ManagerLevel>
        )}
        {levelsToAnimate.map((levelToAnimate, index) => (
          <Fragment key={levelToAnimate.from.levelReached}>
            {levelStep === index && (
              <StyledLevelBar
                animated={animated}
                animatedPoints={animatedPoints}
                managerProgression={{
                  ...managerProgression,
                  levelReached: managerProgression.levelReached,
                }}
              />
            )}
          </Fragment>
        ))}
        {[ExperienceBarStep.LEVELUP, ExperienceBarStep.REWARDS].includes(
          step
        ) && <YellowFog />}
        {step === ExperienceBarStep.LEVELUP && (
          <LevelUpContainer>
            <EmojiFlood
              fullHeight
              emoji={
                <FontAwesomeIcon
                  icon={faLongArrowAltUp}
                  color="rgba(var(--c-rgb-neutral-1000), .2)"
                  size="3x"
                />
              }
            />
            <LevelUpText>
              <Horizontal>
                <Letter src={l} alt="" />
                <Letter src={e} alt="" />
                <Letter src={v} alt="" />
                <Letter src={e} alt="" />
                <Letter src={l} alt="" />
              </Horizontal>
              <Horizontal>
                <Letter src={u} alt="" />
                <Letter src={p} alt="" />
                <Letter src={exclamation} alt="" />
              </Horizontal>
            </LevelUpText>
          </LevelUpContainer>
        )}
        {step === ExperienceBarStep.REWARDS && (
          <RewardsContainer>
            <Vertical gap={8}>
              <RewardTitle>
                <FormattedMessage {...glossary.rewards} />
              </RewardTitle>
              <Rewards gap={8}>
                {backgroundsWon.length > 0 && (
                  <RewardBlock center style={{ [rewardBlocKIndex]: 0 }}>
                    <RewardImgContainer>
                      <RewardImg src={backgroundsWon[0].pictureUrl} alt="" />
                    </RewardImgContainer>
                    <RewardCount>x{backgroundsWon.length}</RewardCount>
                    <RewardText>
                      <FormattedMessage
                        id="Challenges.ClaimBadgeAnimation.stadiums"
                        defaultMessage="Stadiums"
                      />
                    </RewardText>
                  </RewardBlock>
                )}
                {clubBadgesWon.length > 0 && (
                  <RewardBlock center style={{ [rewardBlocKIndex]: 1 }}>
                    <RewardImgContainer>
                      <RewardImg src={clubBadgesWon[0].pictureUrl} alt="" />
                    </RewardImgContainer>
                    <RewardCount>x{clubBadgesWon.length}</RewardCount>
                    <RewardText>
                      <FormattedMessage
                        id="Challenges.ClaimBadgeAnimation.badges"
                        defaultMessage="Badges"
                      />
                    </RewardText>
                  </RewardBlock>
                )}
              </Rewards>
              <ContinueBtn color="primary" size="small" onClick={endAnimation}>
                <FormattedMessage {...glossary.continue} />
              </ContinueBtn>
            </Vertical>
          </RewardsContainer>
        )}
        {step !== ExperienceBarStep.REWARDS && (
          <Points>
            +<FormattedNumber value={pointsEarned} />{' '}
            <FormattedMessage {...glossary.mp} />
          </Points>
        )}
      </Content>
    </Root>
  );
};
